import { useEffect, useId, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil, Plus, Power, PowerOff, Search, UsersRound } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ErrorState } from "@/components/shared/ErrorState";
import { customerGroupsQueryKey } from "@/components/shared/customerGroupsQueryKey";
import { EmptyState } from "@/pages/CustomerPortal/Financial/component/EmptyState";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { useDebounce } from "@/hooks/useDebounce";
import { CustomerGroup, CustomerGroupStatusFilter, customerGroupsApi, SaveCustomerGroupDto } from "@/lib/api";
import { getCustomerRegistrationErrorMessage } from "@/lib/customerRegistrationError";
import { formatDateTime } from "@/lib/formatters";
import { CustomerGroupDialog } from "./component/CustomerGroupDialog";
import { PAGE_SIZES } from "./component/pagination";
import { TablePagination } from "./component/TablePagination";

type StatusFilter = CustomerGroupStatusFilter;

const STATUS_LABELS: Record<StatusFilter, string> = {
  active: "Ativos",
  inactive: "Inativos",
  all: "Todos",
};

// Sob customerGroupsQueryKey: qualquer alteração invalida também o select de grupo da tela de cliente.
const listQueryKey = [...customerGroupsQueryKey, "all"] as const;

/**
 * Backoffice > Clientes > Grupos de cliente: cadastro dos grupos (listar, filtrar, criar, editar, desativar e
 * reativar). Pesquisa, filtro de situação e paginação são feitos na API. Grupo nunca é apagado; desativar é recusado
 * pela API enquanto houver cliente vinculado.
 */
export default function CustomerGroupsList() {
  const searchId = useId();
  const statusId = useId();
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("active");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[1]);
  const debouncedSearch = useDebounce(search.trim(), 300);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CustomerGroup | null>(null);

  const groupsQuery = useQuery({
    queryKey: [...listQueryKey, { search: debouncedSearch, status, page, pageSize }],
    queryFn: () => customerGroupsApi.list({ search: debouncedSearch, status, page, pageSize }),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: customerGroupsQueryKey });

  // O diálogo trata os erros (campos e toast); aqui só o sucesso.
  const saveMutation = useMutation({
    mutationFn: (group: SaveCustomerGroupDto) =>
      editing ? customerGroupsApi.update(editing.id, group) : customerGroupsApi.create(group),
    onSuccess: async () => {
      toast.success(editing ? "Grupo atualizado." : "Grupo cadastrado.");
      await invalidate();
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ group, active }: { group: CustomerGroup; active: boolean }) =>
      active ? customerGroupsApi.reactivate(group.id) : customerGroupsApi.deactivate(group.id),
    onSuccess: async (_, { active }) => {
      toast.success(active ? "Grupo reativado." : "Grupo desativado.");
      await invalidate();
    },
    onError: (error) => toast.error(getCustomerRegistrationErrorMessage(error, "Não foi possível alterar a situação do grupo.")),
  });

  const groups = groupsQuery.data?.data ?? [];
  const total = groupsQuery.data?.total ?? 0;

  // Desativar o último grupo da página (com filtro de ativos) deixa a página vazia: volta para a última que existe.
  useEffect(() => {
    if (!groupsQuery.isPlaceholderData && page > 1 && groups.length === 0 && total > 0)
      setPage(Math.ceil(total / pageSize));
  }, [groupsQuery.isPlaceholderData, page, groups.length, total, pageSize]);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (group: CustomerGroup) => {
    setEditing(group);
    setDialogOpen(true);
  };

  const toggleStatus = async (group: CustomerGroup) => {
    const active = !group.isActive;
    const confirmed = await confirm(active
      ? {
          title: "Reativar grupo?",
          description: `O grupo ${group.code} volta a aparecer na escolha de grupo dos clientes.`,
          confirmLabel: "Reativar",
        }
      : {
          title: "Desativar grupo?",
          description: `O grupo ${group.code} deixa de aparecer na escolha de grupo dos clientes. Ele não é apagado e pode ser reativado.`,
          confirmLabel: "Desativar",
          variant: "destructive",
        });
    if (confirmed) statusMutation.mutate({ group, active });
  };

  // Sem filtro nenhum e sem resultado: não há grupo cadastrado.
  const noGroupsAtAll = total === 0 && !debouncedSearch && status === "all";

  return (
    <div className="space-y-6 p-4 md:p-6">
      <ConfirmDialog />
      <CustomerGroupDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        group={editing}
        isSubmitting={saveMutation.isPending}
        onSave={(group) => saveMutation.mutateAsync(group)}
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <UsersRound className="h-6 w-6 text-primary" aria-hidden="true" />
            <h1 className="text-2xl font-bold tracking-tight">Grupos de cliente</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Cadastre e mantenha os grupos usados no cadastro dos clientes. Grupos não são apagados: desative os que não
            são mais usados.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
          Novo grupo
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="grid gap-3 sm:grid-cols-[1fr_200px]">
            <div className="space-y-1">
              <Label htmlFor={searchId} className="sr-only">Buscar grupo</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <Input
                  id={searchId}
                  value={search}
                  onChange={(event) => {
                    setSearch(event.target.value);
                    setPage(1);
                  }}
                  placeholder="Buscar por código, nome ou ID do Magento..."
                  className="pl-9"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor={statusId} className="sr-only">Situação</Label>
              <Select
                value={status}
                onValueChange={(value) => {
                  setStatus(value as StatusFilter);
                  setPage(1);
                }}
              >
                <SelectTrigger id={statusId}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(STATUS_LABELS) as StatusFilter[]).map((value) => (
                    <SelectItem key={value} value={value}>{STATUS_LABELS[value]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {groupsQuery.isLoading ? (
            <div className="space-y-2" aria-busy="true">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : groupsQuery.isError ? (
            <ErrorState message="Não foi possível carregar os grupos." onRetry={() => void groupsQuery.refetch()} />
          ) : noGroupsAtAll ? (
            <EmptyState title="Nenhum grupo cadastrado" message="Clique em Novo grupo para cadastrar o primeiro." />
          ) : groups.length === 0 ? (
            <EmptyState message="Nenhum grupo com esses filtros." />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead scope="col">Código</TableHead>
                  <TableHead scope="col">Nome</TableHead>
                  <TableHead scope="col">ID Magento</TableHead>
                  <TableHead scope="col" className="text-right">Clientes</TableHead>
                  <TableHead scope="col">Situação</TableHead>
                  <TableHead scope="col">Atualizado em</TableHead>
                  <TableHead scope="col" className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups.map((group) => {
                  const blocked = group.isActive && group.clientCount > 0;
                  const updatedAt = group.updatedAt ?? group.importedAt ?? group.createdAt;
                  return (
                    <TableRow key={group.id}>
                      <TableCell className="font-mono">{group.code}</TableCell>
                      <TableCell>{group.name}</TableCell>
                      <TableCell className="font-mono">{group.magentoGroupId ?? "—"}</TableCell>
                      <TableCell className="text-right">{group.clientCount}</TableCell>
                      <TableCell>
                        <Badge variant={group.isActive ? "success" : "secondary"}>{group.isActive ? "Ativo" : "Inativo"}</Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {updatedAt ? formatDateTime(new Date(updatedAt)) : "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(group)} aria-label={`Editar ${group.code}`}>
                            <Pencil className="mr-1 h-4 w-4" aria-hidden="true" />
                            Editar
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => void toggleStatus(group)}
                            disabled={blocked || statusMutation.isPending}
                            title={blocked ? "Mova os clientes para outro grupo antes de desativar." : undefined}
                            aria-label={`${group.isActive ? "Desativar" : "Reativar"} ${group.code}`}
                          >
                            {group.isActive
                              ? <PowerOff className="mr-1 h-4 w-4" aria-hidden="true" />
                              : <Power className="mr-1 h-4 w-4" aria-hidden="true" />}
                            {group.isActive ? "Desativar" : "Reativar"}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          {!groupsQuery.isLoading && !groupsQuery.isError && total > 0 && (
            <div className="mt-4 space-y-3">
              <TablePagination
                page={page}
                pageSize={pageSize}
                total={total}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
                disabled={groupsQuery.isPlaceholderData}
              />
              <p className="text-xs text-muted-foreground">Grupos com clientes vinculados não podem ser desativados.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
